import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { ScribeButton as Button } from '../../components/scribe/ScribeUI';
import { loadClientConsentLog } from '../../services/clientConsentService';
import ClientConsentPanel from './ClientConsentPanel';

const scope = 'MindVault profile shared with this therapist';
const date = value => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime())
    ? parsed.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
    : 'Not recorded';
};

export default function ClientConsentLog({ patientId, clientEmail, userId }) {
  return patientId ? <ClientConsentPanel key={`${patientId}:${userId || ''}`} patientId={patientId} /> : <MindVaultConsentHistory clientEmail={clientEmail} />;
}

function MindVaultConsentHistory({ clientEmail }) {
  const [offset, setOffset] = useState(0);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const email = (clientEmail || '').trim().toLowerCase();
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true); setError(''); setRecord(null);
    if (!email) { setLoading(false); return; }
    try {
      const data = await loadClientConsentLog(email, offset);
      if (current === generation.current) setRecord({ ...data, email });
    } catch (e) { if (current === generation.current) setError(e.message); }
    finally { if (current === generation.current) setLoading(false); }
  }, [email, offset]);
  useEffect(() => { setOffset(0); }, [email]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load]);
  useEffect(() => {
    const refreshOnReturn = () => { void load(); };
    window.addEventListener('focus', refreshOnReturn);
    return () => window.removeEventListener('focus', refreshOnReturn);
  }, [load]);
  const visible = record?.email === email ? record : null;
  return <Box component="section" aria-label="MindVault sharing consent log" sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.5, mt: 2.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>MindVault sharing consent log</Typography>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Recorded changes to this client’s MindVault profile sharing with you.</Typography>
    {!email ? <Typography variant="body2" color="text.secondary">Add an email to this client’s profile to look up MindVault sharing records.</Typography> : loading ?
      <CircularProgress size={24} aria-label="Loading sharing records" /> : error ?
      <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert> : visible && <>
        <Chip variant="outlined" size="small" color={visible.sharing ? 'success' : 'default'} label={visible.sharing ? 'Sharing on' : 'Sharing off'} sx={{ mb: 2 }} />
        {!visible.events.length ? <Typography variant="body2" color="text.secondary">No dated sharing changes were recorded. Earlier consent or withdrawal dates may not be available.</Typography> :
          <Stack spacing={1.5}>{visible.events.map(event => {
            const granted = event.event === 'consent_granted';
            return <Box key={event.id} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{granted ? 'Profile sharing consent given' : 'Profile sharing consent withdrawn'}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box><Typography variant="caption" color="text.secondary">{granted ? 'Consent date' : 'Withdraw consent date'}</Typography><Typography variant="body2">{date(event.created_at)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Scope</Typography><Typography variant="body2">{scope}</Typography></Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Source: MindVault profile sharing</Typography>
            </Box>;
          })}</Stack>}
        {visible.total > 25 && <Stack direction="row" gap={1.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap" sx={{ mt: 2 }}>
          <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</Button>
          <Typography variant="body2">{offset + 1}–{Math.min(offset + 25, visible.total)} of {visible.total}</Typography>
          <Button size="small" disabled={offset + 25 >= visible.total} onClick={() => setOffset(offset + 25)}>Next</Button>
        </Stack>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>History includes recorded sharing changes only. It does not grant access to a withdrawn profile.</Typography>
      </>}
  </Box>;
}
